import React from 'react';
import { Image, Tooltip } from '@fluentui/react-components';
import { isCapabilityEnabled } from 'owa-capabilities';
import { headerClickableTenantLogoCapability } from 'owa-capabilities-definitions/lib/headerClickableTenantLogoCapability';
import classNames from 'owa-classnames';
import loc from 'owa-localize';
import { observer } from 'owa-mobx-react';
import type NavBarData from 'owa-service/lib/contract/NavBarData';
import { getCurrentThemeId } from 'owa-theme';
import { shouldUseCobranding } from 'owa-theme-common';
import { TENANT_LOGO_ID } from '../../constants/constants';
import getTenantLogoData from '../../utils/charms/getTenantLogoData';
import { clickableTenantLogo, tenantLogo } from '../OwaHeader.scss';
import { tenantLogoTooltip } from '../OwaHeader.locstring.json';

interface OwaHeaderTenantLogoProps {
    navBarData: NavBarData | null;
}

const defaultExport: React.FunctionComponent<OwaHeaderTenantLogoProps> = observer(
    function OwaHeaderTenantLogo(props: OwaHeaderTenantLogoProps) {
        const { navBarData } = props;
        const themeId = getCurrentThemeId();
        const shouldShowTenantLogo = shouldUseCobranding(themeId);
        const tenantLogoData = shouldShowTenantLogo ? getTenantLogoData(navBarData) : undefined;
        const navigationUrl = tenantLogoData?.navigationUrl;

        const handleLogoClick = React.useCallback(() => {
            if (navigationUrl) {
                window.location.href = navigationUrl;
            }
        }, [navigationUrl]);

        const handleKeyDown = React.useCallback(
            (event: React.KeyboardEvent) => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    handleLogoClick();
                }
            },
            [handleLogoClick]
        );

        if (!shouldShowTenantLogo || !tenantLogoData?.logoUrl) {
            return null;
        }

        const isClickable =
            !!navigationUrl && isCapabilityEnabled(headerClickableTenantLogoCapability);
        const tooltipContent = loc(tenantLogoTooltip);

        const imageElement = (
            <Image
                id={TENANT_LOGO_ID}
                className={classNames(tenantLogo, isClickable && clickableTenantLogo)}
                src={tenantLogoData.logoUrl}
                alt={tooltipContent}
                role={isClickable ? 'button' : 'img'}
                tabIndex={isClickable ? 0 : -1}
                onClick={isClickable ? handleLogoClick : undefined}
                onKeyDown={isClickable ? handleKeyDown : undefined}
            />
        );

        return isClickable ? (
            <Tooltip content={tooltipContent} relationship="description">
                {imageElement}
            </Tooltip>
        ) : (
            imageElement
        );
    },
    'OwaHeaderTenantLogo'
);
export default defaultExport;
